import axios from 'axios';
import API_CONFIG from '../config/api';

// API base URL from configuration
const API_URL = API_CONFIG.baseURL;

// Function to get auth headers with token
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
    },
  };
};

// Interface for User object
interface User {
  _id: string;
  name: string;
  lastName?: string;
  email?: string;
  mobile: string;
  nationalId?: string;
  status: 'active' | 'suspended';
  userType: 'Admin' | 'User';
  createdAt: string;
}

// Interface for pagination data
interface PaginationData {
  currentPage: number;
  total: number;
  totalPages: number;
}

// Interface for pagination response
interface PaginatedResponse {
  success: boolean;
  message?: string;
  data: User[];
  pagination: PaginationData;
}

/**
 * Get list of users (admin only)
 */
export const getUsers = async (params: {
  userType?: string;
  page?: number;
  limit?: number;
  status?: string;
}) => {
  try {
    const response = await axios.get(
      API_CONFIG.getUrl('admin/users'),
      {
        params,
        ...getAuthHeaders(),
      }
    );
    
    // Handle the actual response structure from the API
    // Return it in a consistent format that matches our interfaces
    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || [],
      pagination: {
        currentPage: parseInt(response.data.page) || 1,
        total: response.data.count || 0,
        totalPages: response.data.totalPages || 1
      }
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Update user status (active/suspended) (admin only)
 */
export const updateUserStatus = async (userId: string, status: 'active' | 'suspended') => {
  try {
    const response = await axios.put(
      API_CONFIG.getUrl(`admin/users/${userId}/status`),
      { status },
      getAuthHeaders()
    );
    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || null
    };
  } catch (error) {

    throw error;
  }
};

/**
 * Update user details (admin only)
 */
interface UserUpdateData {
  name?: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  nationalId?: string;
  password?: string;
  confirmPassword?: string;
  changePassword?: boolean;
}

export const updateUserDetails = async (userId: string, userData: UserUpdateData) => {
  try {
    const response = await axios.put(
      API_CONFIG.getUrl(`admin/users/${userId}`),
      userData,
      getAuthHeaders()
    );
    return {
      success: response.data.success,
      message: response.data.message,
      data: response.data.data || null
    };
  } catch (error: any) {

    // Return error response in the same format as success response
    if (error.response && error.response.data) {
      return {
        success: false,
        message: error.response.data.message || 'خطا در بروزرسانی اطلاعات کاربر',
        data: null
      };
    }
    // If no response data, throw the error
    throw error;
  }
};

// Check if mobile, email, or nationalId already exists
export const checkUserDuplicate = async (mobile?: string, email?: string, nationalId?: string, userId?: string) => {
  try {
    const response = await axios.post(
      API_CONFIG.getUrl('admin/users/check-duplicate'),
      { mobile, email, nationalId, userId },
      getAuthHeaders()
    );
    return {
      success: response.data.success,
      errors: response.data.errors || {}
    };
  } catch (error: any) {

    return {
      success: false,
      errors: {}
    };
  }
};

// Get current user profile (using original endpoint)
export const getUserProfile = async () => {
  try {
    const response = await axios.get(`${API_URL}/users/get_me`, getAuthHeaders());
    return response.data;
  } catch (error) {

    throw error;
  }
};

// Get current user using the /me endpoint (recommended for new code)
export const getCurrentUser = async () => {
  try {
    const response = await axios.get(`${API_URL}/users/me`, getAuthHeaders());
    return response.data;
  } catch (error) {

    throw error;
  }
};

// Update user profile with optional avatar
export const updateUserProfile = async (profileData: FormData) => {
  try {
    const token = localStorage.getItem('authToken');
    const response = await axios.put(
      `${API_URL}/users/profile`,
      profileData,
      {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          // Don't set Content-Type for multipart/form-data as axios will set it automatically with the boundary
        },
      }
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

// Update or add user password
export const updatePassword = async (passwordData: { password: string; confirmPassword: string }) => {
  try {
    const response = await axios.put(
      `${API_URL}/users/update-password`,
      passwordData,
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};

// Convert Persian/Arabic numbers to Latin
export const convertPersianToLatinNumbers = (input: string): string => {
  if (!input) return '';
  
  const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
  const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
  const latinDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return input.split('').map(char => {
    const persianIndex = persianDigits.indexOf(char);
    if (persianIndex !== -1) return latinDigits[persianIndex];
    
    const arabicIndex = arabicDigits.indexOf(char);
    if (arabicIndex !== -1) return latinDigits[arabicIndex];
    
    return char;
  }).join('');
};

// Normalize mobile number (based on server-side function)
export const normalizeMobileNumber = (mobile: string): string => {
  if (!mobile) return '';
  
  // Convert Persian/Arabic numbers to Latin
  let normalizedMobile = convertPersianToLatinNumbers(mobile);
  // Remove non-digit characters
  normalizedMobile = normalizedMobile.replace(/\D/g, '');
  
  // Add leading zero if missing (for Iranian numbers)
  if (normalizedMobile.length === 10 && normalizedMobile.startsWith('9')) {
    normalizedMobile = '0' + normalizedMobile;
  }
  
  // Convert from international format to local format
  if (normalizedMobile.startsWith('98') && normalizedMobile.length >= 12) {
    normalizedMobile = '0' + normalizedMobile.substring(2);
  }
  
  // Return the normalized mobile number
  return normalizedMobile;
};

// Get family information for current user
export const getFamily = async () => {
  try {
    const response = await axios.get(`${API_URL}/users/family`, getAuthHeaders());
    return response.data;
  } catch (error) {

    throw error;
  }
};

// Update family information
export const updateFamily = async (familyData: any) => {
  try {
    const response = await axios.put(
      `${API_URL}/users/family`,
      { family: familyData },
      getAuthHeaders()
    );
    return response.data;
  } catch (error) {

    throw error;
  }
};
